import { useNavigationParams } from './navigation/history'
import ContextFundOverview from './navigation/ContextFundOverview'
import { FundReportingPanel } from './navigation/FundReporting'
import { useContextualNavigation } from './navigation/useRead'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { apiFetch } from '../lib/api'
import { getToken, decodeToken } from '../lib/auth'
import type { TaskQueueItem } from '../lib/tasks'
import ObjectDrawer, { DrawerTarget } from './ObjectDrawer'
import { fetchFundLedger, fetchFundLedgers } from './ledger'
import { openWords, type FundLedger } from './ledgerWords'
import FundSelector from './FundSelector'
import { rememberFundChoice } from './fundScope'
import type { RecordTablePayload } from './RecordTable'
import FundOverviewTab from './fund/FundOverviewTab'
import FundRecordTab from './fund/FundRecordTab'
import FundEvidenceTab from './fund/FundEvidenceTab'
import {
  fetchEvidenceMatrix,
  fetchWalkStatus,
  fetchMasters,
  type Lens,
  type MatrixRow,
  type WalkNode,
} from './fund/fundArea'
import type { MasterLink } from './fund/masters'
import { evidenceCounts, type EvShow } from './fund/evidenceRows'
import RebuildToggle, { type RebuildState } from './fund/RebuildToggle'
import { rebuildRecord, historyDates } from './fund/rebuildRecord'
import { droppedLegsOf, isIsoDate, outsideDemands, type AsOfPayload } from './fund/asOf'
import type { AsOfView } from './fund/fundArea'
import { formatOfficerDate } from '../lib/officerFormat'
import {
  chainOf,
  focusForTarget,
  parseFocus,
  sectionSentence,
  serializeFocus,
  targetForFocus,
  type Focus,
} from './fund/focus'
import { fetchOpenTasks } from './workbench/api'
import { HelpDot } from './guidance/Guidance'
import { GUIDANCE_CONTENT } from './guidance/content'
import './fund/fundtabs.css'
import { EmptyState, ErrorState, Skeleton } from './state'

/* Fund cockpit — header strip with the real aggregates, the ownership
 * tree (PR #588) as the centerpiece, and the object drawer (ledger
 * screen fund_drawer): any entity/position/trade clicked through from
 * the tree opens its dossier — facts, evidence, history. */

interface FundResponse {
  id: string
  name: string
  legal_form: string | null
  jurisdiction: string | null
  verification_pct: number
  verification_status: string
  conflict_count: number
  open_event_count: number
  expiring_count: number
  next_deadline: string | null
  /** D-3b honest-verified: open MANDATORY obligations (incl. trades) +
   *  chain paths not yet verified — both must be 0 for the ring to
   *  claim verified. */
  open_mandatory_count?: number
  unverified_path_count?: number
  /** Ale ruling 2026-07-26: open reconciliation breaks — the record's
   *  arithmetic doesn't tie out. Blocks sign-off and holds the ring
   *  below 100 on its own; NOT an evidence gap (no obligation, no
   *  chase raised until the officer resolves the break). */
  open_reconciliation_count?: number
  /** Officer progress inputs for the ring (Ale ruling 2026-07-28). */
  mandatory_satisfied_count?: number
  mandatory_total_count?: number
  /** Demo-v2 W1 register chip: 'registered' (under agreement) /
   *  'unregistered' (depositary-jurisdiction fund, no agreement on the
   *  register) / null-ish (flag off, or foreign fund — never labeled). */
  register_status?: 'registered' | 'unregistered' | null
  /** D7 (mig 165): where chase letters go — captured at fund creation. */
  client_contact_name?: string | null
  client_contact_email?: string | null
  /** RECORD START (Ale 2026-09-04, mig 170): the date the record of this
   *  fund begins — nothing dated before it is demanded or chased. Null =
   *  not yet set, everything demanded. */
  record_start_date?: string | null
}

type Tab = 'ov' | 'rec' | 'ev'
const TAB_PARAM: Record<Tab, string> = { ov: 'overview', rec: 'record', ev: 'evidence' }
const TAB_LABEL: Record<Tab, string> = { ov: 'Overview', rec: 'Record', ev: 'Evidence' }
const TABS: Tab[] = ['ov', 'rec', 'ev']
const PARAM_TAB: Record<string, Tab> = { overview: 'ov', record: 'rec', evidence: 'ev' }

export default function FundCockpit() {
  const [params] = useNavigationParams()
  const capability = useContextualNavigation()
  if (capability.loading)
    return (
      <main className="detail" role="status">
        Loading fund…
      </main>
    )
  if (capability.error)
    return (
      <main className="detail">
        <ErrorState what="fund navigation" error={capability.error} />
      </main>
    )
  // Preserve existing document/evidence bookmarks until the new decision route
  // owns this alias. The contextual page itself has no separate Evidence tab.
  return capability.value?.enabled && params.get('tab') !== 'evidence' ? (
    <ContextFundOverview />
  ) : (
    <LegacyFundCockpit />
  )
}
function LegacyFundCockpit() {
  /* The fund area — THREE tabs, three questions (locked 2026-08-31):
   * Overview (where am I?) · Record (does it add up?) · Evidence (how
   * is it proven?). Header = name + meta + contact + tabs ONLY — no
   * open-items panel, no ring (ruled three times); open work appears
   * solely as tab badges and the counts-doors inside the tabs.
   * FUND TABS ARE SYMBOL-FREE. Structure/Objects/Requests/Events tabs
   * retired per the scaffolding (events history → Documents).
   *
   * FOCUS MODEL (Ale ruling 2026-09-02): the tab and the focused object
   * live in the URL (`?tab=record&focus=entity:<id>`), so a click on the
   * diagram, a record row, or an evidence link is a place the officer can
   * return to. The focused object's VERTICAL SECTION — itself and its
   * ancestors up to the fund — is what the diagram lights and the record
   * keeps; the drawer names it in a sentence. See fund/focus.ts. */
  const { id } = useParams<{ id: string }>()
  const [params, setParams] = useNavigationParams()
  const navigate = useNavigate()
  const [fund, setFund] = useState<FundResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [drawer, setDrawer] = useState<DrawerTarget | null>(null)
  /* Which focus the drawer was last opened for, so a URL focus opens the
   * drawer exactly once. Closing the drawer CLEARS the focus (F2, Ale
   * 2026-09-03): the URL forgets it and the dimming lifts — the chip's
   * "clear" stays as the explicit route too. */
  const drawerFor = useRef<string | null>(null)

  const tab: Tab = PARAM_TAB[params.get('tab') ?? ''] ?? 'ov'
  const focusRaw = params.get('focus')
  /* `show=owed` / `proven` / `optional` / `waived`: the Evidence tab's
   * count chip — the door the chain-status headline opens (Ale
   * 2026-09-03). Anything else = every line. */
  const showRaw = params.get('show')
  const show: EvShow | null =
    showRaw === 'owed' || showRaw === 'proven' || showRaw === 'optional' || showRaw === 'waived'
      ? showRaw
      : null

  const [recordTable, setRecordTable] = useState<RecordTablePayload | 'error' | null>(null)
  const [recordFailure, setRecordFailure] = useState<unknown>(null)
  const [matrix, setMatrix] = useState<MatrixRow[]>([])
  const [walk, setWalk] = useState<WalkNode[]>([])
  const [masters, setMasters] = useState<MasterLink[]>([])
  const [tasks, setTasks] = useState<TaskQueueItem[]>([])
  /* The fund's ledger (B1): the banner, the chain-status headline and
   * the Evidence header all print ITS figures. */
  const [ledger, setLedger] = useState<FundLedger | null>(null)
  /* Every fund's ledger — the selector in this page's title counts from
   * the same bulk read every other screen counts from. */
  const [ledgers, setLedgers] = useState<FundLedger[] | null>(null)

  /* The SHOW control (Ale C8 2026-09-03 + R3 2026-09-04): today ·
   * evidenced only · as of a date. The AS-OF DATE lives in the URL
   * (`?asof=2026-05-31`) — a place the officer can return to, like the
   * tab and the focus. Evidenced-only stays a lens: it resets on every
   * fund open. The control governs the three tab contents; the counts
   * strip, the workbench queue and Export stay on the full record. */
  const asOfRaw = params.get('asof')
  const asOf = isIsoDate(asOfRaw) ? asOfRaw : null
  const [evidenced, setEvidenced] = useState(false)
  useEffect(() => {
    setEvidenced(false)
  }, [id])
  const rebuild: RebuildState = useMemo(
    () =>
      asOf
        ? { mode: 'asof', asOf }
        : evidenced
          ? { mode: 'evidenced', asOf: null }
          : { mode: 'full', asOf: null },
    [asOf, evidenced]
  )

  /* `background === true` only from the heartbeat below — the idle clock
   * (lib/api.ts) must not move on a timer. */
  const load = useCallback(
    (background = false) => {
      if (!id) return
      const bg = background === true
      apiFetch<FundResponse>(`/api/funds/${id}`, { background: bg })
        .then(setFund)
        .catch((e) => setError(e instanceof Error ? e.message : 'failed'))
      apiFetch<RecordTablePayload>(`/api/funds/${id}/record-table?state=all`, { background: bg })
        .then((t) => {
          setRecordTable(t)
          setRecordFailure(null)
        })
        .catch((e: unknown) => {
          setRecordFailure(e)
          setRecordTable((cur) => (cur && cur !== 'error' ? cur : 'error'))
        })
      fetchEvidenceMatrix(id, bg)
        .then(setMatrix)
        .catch(() => setMatrix([]))
      fetchWalkStatus(id, bg)
        .then(setWalk)
        .catch(() => setWalk([]))
      // The feeder → masters link (16a); an ordinary fund has none, and the
      // endpoint is a backend follow-up, so any error is "no masters".
      fetchMasters(id, bg)
        .then(setMasters)
        .catch(() => setMasters([]))
      fetchOpenTasks(id, bg)
        .then(setTasks)
        .catch(() => setTasks([]))
      fetchFundLedger(id, bg)
        .then(setLedger)
        .catch(() => setLedger(null))
    },
    [id]
  )

  // Retry (state system): re-runs the effect — a fresh read and a fresh
  // heartbeat clock, never a double fetch.
  const [tick, setTick] = useState(0)
  useEffect(() => {
    load()
    const t = setInterval(() => load(true), 20000)
    return () => clearInterval(t)
  }, [load, tick])
  const retryRecord = () => {
    setRecordTable(null)
    setRecordFailure(null)
    setTick((t) => t + 1)
  }

  /* THE RECORD AS OF A DATE (R3, Ale 2026-09-04) — the engine's ONE rule
   * (funds/as_of_scope), served by GET /api/funds/{ref}/as-of: the record
   * table and the matrix are already the D view; `dropped` names what it
   * left out. Fetched whenever the URL carries a date, refreshed on the
   * same heartbeat as the rest; a late answer for a date no longer in the
   * URL is thrown away. */
  type AsOfState =
    | { date: string; view: AsOfPayload<RecordTablePayload, MatrixRow> }
    | { date: string; error: true }
  const [asOfState, setAsOfState] = useState<AsOfState | null>(null)
  const asOfWanted = useRef<string | null>(asOf)
  asOfWanted.current = asOf
  const loadAsOf = useCallback(
    (background = false) => {
      if (!id || !asOf) return
      const date = asOf
      apiFetch<AsOfPayload<RecordTablePayload, MatrixRow>>(
        `/api/funds/${id}/as-of?date=${date}&state=all`,
        { background: background === true }
      )
        .then((view) => {
          if (asOfWanted.current === date) setAsOfState({ date, view })
        })
        .catch(() => {
          if (asOfWanted.current === date) setAsOfState({ date, error: true })
        })
    },
    [id, asOf]
  )
  useEffect(() => {
    if (!asOf) {
      setAsOfState(null)
      return
    }
    loadAsOf()
    const t = setInterval(() => loadAsOf(true), 20000)
    return () => clearInterval(t)
  }, [asOf, loadAsOf])
  const asOfReady =
    asOf && asOfState && asOfState.date === asOf && 'view' in asOfState ? asOfState.view : null
  const asOfError = !!(asOf && asOfState && asOfState.date === asOf && 'error' in asOfState)

  /* The record start (Ale 2026-09-04): a manager's onboarding fact, set
   * inline on the Overview facts block. The reply is the fund payload,
   * so the header and facts re-render from one source. */
  const role = decodeToken(getToken() ?? '')?.role ?? 'ops_officer'
  const canSetRecordStart = role === 'manager' || role === 'admin'
  const setRecordStart = useCallback(
    async (iso: string | null) => {
      if (!id) return
      const next = await apiFetch<FundResponse>(`/api/funds/${id}/record-start`, {
        method: 'PATCH',
        body: JSON.stringify({ record_start_date: iso }),
      })
      setFund(next)
    },
    [id]
  )

  /* The fund page holds its fund in the PATH, so the selector navigates
   * instead of writing a param — and still records the choice, so the
   * queue the officer opens next carries the same fund (Tier-2 ruling). */
  useEffect(() => {
    if (id) rememberFundChoice(id)
  }, [id])
  useEffect(() => {
    let cancelled = false
    fetchFundLedgers()
      .then((l) => {
        if (!cancelled) setLedgers(l.funds)
      })
      .catch(() => {
        if (!cancelled) setLedgers(null)
      })
    return () => {
      cancelled = true
    }
  }, [])
  const chooseFund = useCallback(
    (next: string | null) => {
      rememberFundChoice(next)
      // "All funds" on a fund page is the Funds list — the page that is
      // every fund. A fund is its own page, with the tab kept.
      if (!next) return navigate('/v2/funds')
      const tab = params.get('tab')
      navigate(`/v2/funds/${encodeURIComponent(next)}${tab ? `?tab=${tab}` : ''}`)
    },
    [navigate, params]
  )
  const selectorFunds = useMemo(
    () =>
      (ledgers ?? []).map((f) => ({
        id: f.fund_id,
        name: f.fund_name,
        open: f.total_open,
        sub: openWords(f),
      })),
    [ledgers]
  )

  const table = recordTable !== null && recordTable !== 'error' ? recordTable : null
  const chain = useMemo(() => (table ? chainOf(table.segments) : []), [table])
  const knownHeadId = useMemo(
    () => table?.entities.find((e) => e.is_fund_root && e.state !== 'proposed')?.id,
    [table]
  )
  const focus = useMemo(() => {
    const parsed = parseFocus(focusRaw)
    // A bookmarked entity:<head> identifies the fund just like a head click,
    // even before a position exists to anchor its identity in the chain.
    return parsed?.kind === 'entity'
      ? focusForTarget(chain, { type: 'entity', id: parsed.id }, null, knownHeadId)
      : parsed
  }, [focusRaw, chain, knownHeadId])

  /* The view the three tabs read (Ale 14b): the evidenced rebuild is
   * client-side (rebuildRecord); the as-of view is the engine's payload,
   * handed through untouched. The focus/drawer plumbing still resolves
   * against the FULL chain above, so a URL focus keeps working even when
   * its object is out of the view. The counts strip, walk queue and Export
   * are handed the full payload as before — the view never touches them. */
  const rebuilt = useMemo(
    () => (table ? rebuildRecord(table, matrix, rebuild.mode) : null),
    [table, matrix, rebuild.mode]
  )
  const viewTable: RecordTablePayload | null = asOf
    ? (asOfReady?.record_table ?? null)
    : (rebuilt?.table ?? table)
  const viewMatrix: MatrixRow[] = asOf
    ? (asOfReady?.matrix.rows ?? [])
    : (rebuilt?.matrix ?? matrix)
  const lens: Lens | null =
    rebuild.mode === 'evidenced'
      ? { mode: 'evidenced', label: 'of the evidenced record' }
      : rebuild.mode === 'asof' && rebuild.asOf
        ? { mode: 'asof', label: `as of ${formatOfficerDate(rebuild.asOf)}` }
        : null
  const today = new Date().toISOString().slice(0, 10)
  /* The Evidence sentence prints the fund LEDGER's figures once read (B1,
   * Ale 2026-09-04) — the same proven / owed / optional the banner, the
   * chain status and every queue print. Under a rebuild lens or an as-of
   * date the rows are narrowed, so the sentence counts the rows it shows. */
  const evidenceFigures =
    ledger && !lens
      ? {
          total: ledger.owed.demanded,
          proven: ledger.owed.proven,
          owed: ledger.owed.total,
          optional: ledger.owed.optional,
          waived: 0,
        }
      : evidenceCounts(viewMatrix)
  /* The dates the record holds — from the full record and, once loaded,
   * the D view (whose legs carry earlier balance dates). R3a serves no
   * fund-level history list yet; this is the client-side derivation. */
  const dates = useMemo(() => historyDates(table, asOfReady?.record_table), [table, asOfReady])
  /* What the D view left out, resolved to the full record's legs so the
   * diagram draws them greyed in place; and the demands of the full
   * record that sit outside the D view's denominator. */
  const asOfView = useMemo<AsOfView | null>(() => {
    if (!asOf || !asOfReady || !table) return null
    const nameOf = (oid: string): string | null => {
      const i = table.instruments.find((x) => x.id === oid)
      if (i) return i.name
      const p = table.positions.find((x) => x.id === oid)
      if (p) return p.instrument_name
      const e = table.entities.find((x) => x.id === oid)
      if (e) return e.name
      const t = table.trades.find((x) => x.id === oid)
      if (t) {
        const anchor = t.position_id
          ? table.positions.find((x) => x.id === t.position_id)?.instrument_name
          : table.instruments.find((x) => x.id === t.instrument_id)?.name
        return anchor ?? null
      }
      return null
    }
    return {
      date: asOfReady.as_of,
      droppedLegs: droppedLegsOf(table, asOfReady.dropped ?? [], asOfReady.as_of),
      notes: asOfReady.notes ?? {},
      outside: outsideDemands(matrix, asOfReady.matrix.rows, nameOf),
      onBackToday: () => setRebuildRef.current({ mode: 'full', asOf: null }),
    }
  }, [asOf, asOfReady, table, matrix])

  /* URL writers — tab, focus, show and the as-of date are the keys we own. */
  const setUrl = useCallback(
    (next: { tab?: Tab; focus?: Focus | null; show?: EvShow | null; asof?: string | null }) => {
      setParams(
        (prev) => {
          const p = new URLSearchParams(prev)
          if (next.tab !== undefined) p.set('tab', TAB_PARAM[next.tab])
          if (next.asof !== undefined) {
            if (next.asof) p.set('asof', next.asof)
            else p.delete('asof')
          }
          if (next.show !== undefined) {
            if (next.show) p.set('show', next.show)
            else p.delete('show')
          }
          if (next.focus !== undefined) {
            if (next.focus) p.set('focus', serializeFocus(next.focus))
            else p.delete('focus')
          }
          return p
        },
        { replace: false }
      )
    },
    [setParams]
  )
  const setTab = (t: Tab) => {
    if (t !== tab) setUrl({ tab: t })
  }
  /* The SHOW control's one writer: the as-of date goes to the URL, the
   * evidenced lens to state; choosing one clears the other. */
  const setRebuild = useCallback(
    (next: RebuildState) => {
      setEvidenced(next.mode === 'evidenced')
      setUrl({ asof: next.mode === 'asof' ? next.asOf : null })
    },
    [setUrl]
  )
  const setRebuildRef = useRef(setRebuild)
  setRebuildRef.current = setRebuild

  /* One door for every click on an object — diagram box, record row,
   * evidence cell: set the focus, open the drawer on that object. */
  const openObject = useCallback(
    (target: DrawerTarget, tab?: Tab) => {
      const tradePos =
        target.type === 'trade'
          ? (table?.trades.find((t) => t.id === target.id)?.position_id ?? null)
          : null
      const f = focusForTarget(chain, target, tradePos, knownHeadId)
      drawerFor.current = f ? serializeFocus(f) : null
      setDrawer(target)
      // One URL write for focus AND tab: two functional writes in a row
      // each start from the live params, so the second (the tab) would
      // carry the focus the first had just replaced (a trade chip jumping
      // to the record kept the OLD focus — caught 2026-09-03).
      setUrl({ focus: f, tab })
    },
    [chain, table, knownHeadId, setUrl]
  )
  const clearFocus = useCallback(() => {
    // Close from the committed URL below. Clearing local state first can
    // leave the drawer shut if immediate Back supersedes this transition
    // with the same focus value React already rendered.
    if (!focusRaw) {
      // Instrument-only movements have no chain focus to put in the URL.
      drawerFor.current = null
      setDrawer(null)
    }
    setUrl({ focus: null })
  }, [focusRaw, setUrl])

  /* Arriving with ?focus= (a link from a document, a bookmark): open the
   * drawer on that object once the record is in. */
  useEffect(() => {
    if (!focus || !focusRaw) {
      drawerFor.current = null
      setDrawer(null)
      return
    }
    if (drawerFor.current === focusRaw) return
    const target = targetForFocus(chain, focus, knownHeadId)
    if (!target) return
    // With no recorded legs, only the actual recorded head can be opened.
    // An arbitrary object or proposed head in a URL must not create a chain.
    if (chain.length === 0 && (target.type !== 'entity' || target.id !== knownHeadId)) return
    drawerFor.current = focusRaw
    setDrawer(target)
  }, [focus, focusRaw, chain, knownHeadId])

  const fundName = table?.fund_name ?? fund?.name ?? 'the fund'
  const entityName = (eid: string) => table?.entities.find((e) => e.id === eid)?.name ?? null
  const section = focus
    ? {
        focus: focusRaw as string,
        tab: TAB_PARAM[tab],
        sentence: sectionSentence(chain, focus, fundName, entityName),
      }
    : null

  return (
    <main className="cockpit fa-page">
      <div className="fa" data-testid="fund-area">
        <div className="fa-head" data-testid="fund-header">
          {/* The fund NAME is the selector (Tier-2 ruling 2026-09-05): the
              same control Today, the Workbench and every queue carry, so a
              fund is reached one way. The header line holds the name and
              form · jurisdiction and nothing else — the register standing
              moved down to the contact line. */}
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
            {/* Back to the portfolio table — the fund page's one way home
                (Tier-2 D3, flat mocks 2026-09-05). */}
            <Link className="fa-back" to="/v2/funds" data-testid="fund-back">
              ‹ Funds
            </Link>
            <span className="fa-name">
              <FundSelector
                funds={selectorFunds}
                value={id ?? null}
                onChoose={chooseFund}
                meta={[fund?.legal_form, fund?.jurisdiction].filter(Boolean).join(' · ') || null}
                loading={ledgers === null}
              />
            </span>
            <span style={{ marginLeft: 'auto' }}>
              <HelpDot k="surface.cockpit" />
            </span>
          </div>
          {/* Who the chases go to. With a contact: the line and nothing more
              (no "signed at fund creation" — no text that does not belong,
              Ale 2026-09-04). Without one: a chip with a door to the chase
              queue's contact capture (?contact=add opens it for this fund
              on arrival), shown only in that state — never a permanent
              sentence sending the officer to another queue (audit cut 14,
              2026-09-04). */}
          <div className="fa-contact" data-testid="fund-contact">
            {fund?.client_contact_email ? (
              <>
                chases go to <b>{fund.client_contact_name ?? fund.client_contact_email}</b>
                {fund.client_contact_name ? <> — {fund.client_contact_email}</> : null}
              </>
            ) : fund ? (
              <Link
                className="nocontact"
                to={`/v2/workbench/chase-request?fund=${encodeURIComponent(fund.id)}&contact=add`}
                data-testid="fund-contact-door"
              >
                no contact stored › add
              </Link>
            ) : null}
            {fund?.register_status === 'unregistered' ? (
              <> · no agreement on register</>
            ) : fund?.register_status === 'registered' ? (
              <> · under agreement</>
            ) : null}
          </div>
          {/* 4A (Ale 2026-09-04): a tab is its label + help dot; the open
              tab's sentence shows ONCE under the bar, the other two live
              behind their dots. The dot sits beside the button — a button
              never nests a button. */}
          <div className="fa-tabs">
            {TABS.map((t) => (
              <span className="fa-tabitem" key={t}>
                <button
                  type="button"
                  className={`fa-tab${tab === t ? ' on' : ''}`}
                  aria-current={tab === t ? 'page' : undefined}
                  onClick={() => setTab(t)}
                  data-testid={`fund-tab-${TAB_PARAM[t]}`}
                >
                  {TAB_LABEL[t]}
                </button>
                <HelpDot k={`fund.tab.${t}`} />
              </span>
            ))}
            {table ? (
              <RebuildToggle value={rebuild} onChange={setRebuild} dates={dates} today={today} />
            ) : null}
          </div>
          <TabSentence tab={tab} counts={tab === 'ev' ? (evidenceFigures ?? null) : null} />
        </div>

        {error ? (
          <div style={{ color: 'var(--fa-gap)', fontSize: 'var(--text-13)' }}>{error}</div>
        ) : null}
        {recordTable === 'error' ? (
          <ErrorState
            what="record"
            error={recordFailure}
            onRetry={retryRecord}
            testId="fund-record-error"
          />
        ) : table === null ? (
          /* The record table's rows, at row height, until the read lands. */
          <Skeleton kind="rows" n={7} height={38} />
        ) : asOf && viewTable === null ? (
          asOfError ? (
            <div className="fa-asof-line" data-testid="rebuild-asof-error">
              <span style={{ color: 'var(--fa-gap)' }}>
                The record as of {formatOfficerDate(asOf)} could not be loaded.
              </span>
              <button
                type="button"
                className="fa-tlink"
                onClick={() => setRebuild({ mode: 'full', asOf: null })}
                data-testid="rebuild-back-today"
              >
                back to today ›
              </button>
              <button
                type="button"
                className="fa-tlink"
                onClick={() => loadAsOf()}
                data-testid="rebuild-asof-retry"
              >
                try again
              </button>
            </div>
          ) : (
            <div className="fa-asof-line" data-testid="rebuild-asof-loading">
              loading the record as of {formatOfficerDate(asOf)}…
            </div>
          )
        ) : tab !== 'ov' && table.segments.length === 0 ? (
          /* Day 0 (audit 248): Record and Evidence say so instead of an
             empty table. Overview draws its own "no chain yet" line. */
          <EmptyState
            sentence="Nothing on the record yet — the record builds as documents book."
            door={{ label: 'Drop documents ›', to: '/v2/traces' }}
            testId="fund-record-empty"
          />
        ) : tab === 'ov' ? (
          <FundOverviewTab
            fundId={id}
            table={viewTable ?? table}
            matrix={viewMatrix}
            walk={walk}
            masters={masters}
            ledger={ledger}
            focus={focus}
            lens={lens}
            asOf={asOfView}
            fund={fund}
            canSetRecordStart={canSetRecordStart}
            onSetRecordStart={setRecordStart}
            onOpen={openObject}
            onClearFocus={clearFocus}
            onJump={(target) => openObject(target, 'rec')}
          />
        ) : tab === 'rec' ? (
          id ? (
            <FundRecordTab
              fundId={id}
              table={viewTable ?? table}
              fullTable={asOf ? (asOfReady?.record_table ?? undefined) : table}
              tasks={tasks}
              masters={masters}
              ledger={ledger}
              matrix={viewMatrix}
              lens={lens}
              onOpen={openObject}
              onEvidence={(target) => openObject(target, 'ev')}
            />
          ) : null
        ) : id ? (
          <FundEvidenceTab
            fundId={id}
            table={viewTable ?? table}
            matrix={viewMatrix}
            tasks={tasks}
            lens={lens}
            onOpen={openObject}
            show={show}
            onShow={(next) => setUrl({ show: next })}
          />
        ) : null}
        {tab === 'ov' && id ? <FundReportingPanel key={id} fundId={id} /> : null}
      </div>
      {/* 3A (Ale 2026-09-04): the drawer OVERLAYS the right edge under a
          light scrim — the page and its tables never reflow; Esc closes it
          and returns focus to the row that opened it. */}
      {drawer && id ? (
        <ObjectDrawer
          fundId={id}
          target={drawer}
          section={section}
          tasks={tasks}
          walk={walk}
          relatedIds={
            drawer.type === 'position'
              ? (chain.find(
                  (s) => s.position_id === drawer.id || s.position_ids.includes(drawer.id)
                )?.position_ids ?? [])
              : []
          }
          onShowOnDiagram={() => setUrl({ tab: 'ov' })}
          onShowInRecord={() => setUrl({ tab: 'rec' })}
          onClose={clearFocus}
        />
      ) : null}
    </main>
  )
}

/** The open tab's one sentence (4A): the question in the lead, the
 *  answer's shape after the dash — the same text its help dot carries.
 *  The Evidence sentence carries the counts (flat v2, Ale 2026-09-05):
 *  "34 of 39 proven · 5 owed, 3 of them optional". */
function TabSentence({
  tab,
  counts,
}: {
  tab: Tab
  counts: ReturnType<typeof evidenceCounts> | null
}) {
  const text = GUIDANCE_CONTENT[`fund.tab.${tab}`] ?? ''
  const cut = text.indexOf(' — ')
  const lead = cut >= 0 ? text.slice(0, cut) : text
  const rest = cut >= 0 ? text.slice(cut + 3) : ''
  const figures =
    counts && counts.total > 0 ? (
      <span data-testid="fund-tab-counts">
        {' '}
        — <b className="m">{counts.proven}</b> of <b className="m">{counts.total}</b> proven
        {counts.owed > 0 ? (
          <>
            {' '}
            · <b className="m">{counts.owed}</b> owed
            {counts.optional > 0 ? (
              <>
                , <b className="m">{counts.optional}</b> of them optional
              </>
            ) : null}
          </>
        ) : null}
      </span>
    ) : null
  return (
    <p className="fa-tab-sentence" data-testid="fund-tab-sentence" data-tab={TAB_PARAM[tab]}>
      <b>{lead}</b>
      {figures}
      {rest ? <> — {rest}</> : null}
    </p>
  )
}
